import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './StallionStatus.css'; // Reuse the same styles
import { CircularProgress } from '@mui/material';

const API_URL = import.meta.env.VITE_API_URL || '';

const StallionCheck = () => {
  const [inputCodes, setInputCodes] = useState('');
  const [scannedCodes, setScannedCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const lastProcessedLength = useRef(0);

  // Auto-focus input for scanner
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentInput = inputCodes.trim();

      if (currentInput) {
        // Check the current input
        checkSingleCodeAndClear(currentInput);
        // Clear the input field
        setInputCodes('');
      }
    }
  };

  const checkSingleCodeAndClear = async (shipCode) => {
    // Don't check if already scanned
    if (scannedCodes.some(existing => existing.shipCode === shipCode)) {
      setError(`Code ${shipCode} already scanned`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/stallion-check`, {
        shipCodes: [shipCode]
      });

      const result = response.data.results[0];

      setScannedCodes(prev => [...prev, {
        ...result,
        timestamp: new Date(),
        id: Date.now() + Math.random()
      }]);

    } catch (err) {
      const errorResult = {
        shipCode: shipCode,
        status: 'ERROR',
        message: `Error checking code: ${err.message}`,
        timestamp: new Date(),
        id: Date.now() + Math.random()
      };

      setScannedCodes(prev => [...prev, errorResult]);
      setError(`Failed to check ${shipCode}: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setInputCodes('');
    setScannedCodes([]);
    setError(null);

    // Refocus input after clearing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'GOING':
        return '#059669'; // Green
      case 'NOT GOING':
        return '#dc2626'; // Red
      case 'NOT FOUND':
        return '#d97706'; // Orange
      default:
        return '#6b7280'; // Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'GOING':
        return '✅';
      case 'NOT GOING':
        return '❌';
      case 'NOT FOUND':
        return '❓';
      case 'ERROR':
        return '⚠️';
      default:
        return '❔';
    }
  };

  const getSummary = () => {
    return {
      total: scannedCodes.length,
      going: scannedCodes.filter(r => r.status === 'GOING').length,
      notGoing: scannedCodes.filter(r => r.status === 'NOT GOING').length,
      notFound: scannedCodes.filter(r => r.status === 'NOT FOUND').length,
      errors: scannedCodes.filter(r => r.status === 'ERROR').length
    };
  };

  return (
    <div className="stallion-status-container">
      <div className="header-section">
        <h1>📱 Stallion QR Scanner Checker</h1>
        <p style={{ color: '#666', marginTop: '0.5rem' }}>
          Scan QR codes to instantly check ship code status
        </p>
      </div>

      {/* Scanner Input Section */}
      <div className="input-section" style={{
        margin: '2rem 0',
        padding: '1.5rem',
        border: '3px solid #3b82f6',
        borderRadius: '12px',
        backgroundColor: '#f0f9ff'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>📱 QR Scanner Input</h2>
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={20} />
              <span style={{ fontSize: '14px', color: '#3b82f6' }}>Checking...</span>
            </div>
          )}
        </div>

        <p style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '16px', fontWeight: '500' }}>
          📍 Ready for scanning - scan QR code and press ENTER to check (input will auto-clear)
        </p>

        <textarea
          ref={inputRef}
          value={inputCodes}
          onChange={(e) => setInputCodes(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="🔍 Scan QR code here... Press ENTER to check and clear input"
          rows="4"
          style={{
            width: '100%',
            padding: '1rem',
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            fontSize: '18px',
            fontFamily: 'monospace',
            backgroundColor: 'white',
            resize: 'vertical'
          }}
        />

        <div style={{ marginTop: '1rem', display: 'flex', gap: '10px' }}>
          <button
            onClick={handleClear}
            className="refresh-button"
            style={{
              backgroundColor: '#dc2626',
              padding: '12px 24px',
              fontSize: '16px'
            }}
          >
            🗑️ Clear All
          </button>

          <div style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            <span>📊 Scanned: {scannedCodes.length}</span>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          margin: '1rem 0',
          padding: '1rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626'
        }}>
          {error}
        </div>
      )}

      {scannedCodes.length > 0 && (
        <div className="results-section">
          {/* Live Summary */}
          <div style={{
            margin: '2rem 0',
            padding: '1.5rem',
            backgroundColor: '#f1f5f9',
            border: '2px solid #10b981',
            borderRadius: '12px'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#059669' }}>📊 Live Results Summary</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '1rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>
                  {getSummary().total}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Total</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#059669' }}>
                  {getSummary().going}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Going ✅</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc2626' }}>
                  {getSummary().notGoing}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Not Going ❌</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#d97706' }}>
                  {getSummary().notFound}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Manual ❓</div>
              </div>
              {getSummary().errors > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc2626' }}>
                    {getSummary().errors}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Errors ⚠️</div>
                </div>
              )}
            </div>
          </div>

          {/* Real-time Results - Most Recent First */}
          <div style={{
            margin: '2rem 0',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              padding: '1rem 1.5rem',
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: 0 }}>📋 Scanned Results ({scannedCodes.length} codes) - Most Recent First</h3>
            </div>

            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {scannedCodes.slice().reverse().map((result, index) => (
                <div key={result.id} style={{
                  padding: '1rem 1.5rem',
                  borderBottom: index < scannedCodes.length - 1 ? '1px solid #e5e7eb' : 'none',
                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    fontSize: '24px',
                    minWidth: '40px',
                    textAlign: 'center'
                  }}>
                    {getStatusIcon(result.status)}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      marginBottom: '4px'
                    }}>
                      {result.shipCode}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      {result.message}
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '4px'
                  }}>
                    <span style={{
                      backgroundColor: `${getStatusColor(result.status)}20`,
                      color: getStatusColor(result.status),
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      {result.status}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      {result.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Export Options */}
          {scannedCodes.length > 0 && (
            <div style={{
              margin: '2rem 0',
              padding: '1rem',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => {
                  const csvContent = "Ship Code,Status,Message,Timestamp\n" +
                    scannedCodes.map(row =>
                      `${row.shipCode},${row.status},"${row.message}",${row.timestamp.toISOString()}`
                    ).join('\n');

                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `stallion_scanner_results_${new Date().toISOString().slice(0, 10)}.csv`;
                  link.click();
                  URL.revokeObjectURL(url);
                }}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                📄 Export All Results CSV
              </button>

              {getSummary().notFound > 0 && (
                <button
                  onClick={() => {
                    const manualCheckItems = scannedCodes.filter(r => r.status === 'NOT FOUND');
                    const csvContent = "Ship Code,Status,Message,Timestamp\n" +
                      manualCheckItems.map(row =>
                        `${row.shipCode},${row.status},"${row.message}",${row.timestamp.toISOString()}`
                      ).join('\n');

                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `stallion_manual_check_${new Date().toISOString().slice(0, 10)}.csv`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{
                    backgroundColor: '#d97706',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  📋 Export Manual Check CSV ({getSummary().notFound})
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StallionCheck;